// ./src/utils/api/EventUpgradeService.ts
//
// ROADMAP #209 — upgrading ONE EVENT to a plan (partylabz sells the event, not a subscription — MONETIZATION §7.3 D1). The
// products are ms_auth's add-on SKUs of type `EVENT_PLAN`, each naming the plan it gives the event; bought on the same
// platform-money rail as a top-up (a PaymentIntent paid in a drawer), at the event's scope.
//
// ⚠ THE ONE PURCHASE THAT NAMES A SCOPE. Top-ups never do (owner, A2.9.2 — `npm run check-usage` keeps the scope out of
// that rail); an event upgrade exists only AT an event, so its scope is built here and nowhere else, from the event id the
// caller passes — never chosen by a person. ms_auth refuses an upgrade SKU at any other scope, and refuses to sell one to
// a subscriber.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

/** The scope ms_auth keeps an event's purchases at. */
const eventScopeOf = (eventId: string): string => `event:${eventId}`;

const UPGRADE_CATALOG_QUERY = gql`
  query EventUpgradeCatalog($params: AppAddonCatalogInput) {
    appAddonCatalog(params: $params) {
      code
      data {
        providerSku
        name
        planCode
        planName
        planFeatures { featureCode featureName featureValueType featureValue status }
        purchasedCount
        limitReached
        owned
        upgradeFromPlanName
        listAmount
        amount
        currency
        purchasable
        amountBeforeOffer
        offerPercentOff
        offerEndsAt
        offerSecondsLeft
      }
      errors { name errors warnings }
    }
  }
`;

const UPGRADE_INTENT_MUTATION = gql`
  mutation EventUpgradePaymentIntentCreate($params: AppAddonPaymentIntentInput) {
    appAddonPaymentIntentCreate(params: $params) {
      code
      data {
        clientSecret
        publishableKey
        paymentIntentId
        amount
        currency
        amountBeforeDiscount
        discountAmount
        discountCode
        granted
      }
      errors { name errors warnings }
    }
  }
`;

/** One plan feature value, as ms_auth states it. */
export interface EventUpgradePlanFeature {
  featureCode: string;
  featureName?: string | null;
  featureValueType?: string | null;
  featureValue?: string | null;
  status?: number | null;
}

/** One upgrade this event can be bought up to. */
export interface EventUpgradeOption {
  providerSku: string;
  /** "VIP for this event". */
  name?: string | null;
  planCode?: string | null;
  planName?: string | null;
  /** What the plan gives the event — which of them covers what it uses is worked out from these (`eventUpgrade.ts`). */
  planFeatures?: EventUpgradePlanFeature[] | null;
  purchasedCount?: number | null;
  /** Not for sale to this event: it holds this plan, or one above it (an event is only ever bought UP — ms_auth). */
  limitReached?: boolean | null;
  /** This event holds this plan now. */
  owned?: boolean | null;
  /** A step up (VIP → Signature): the plan it is bought over — `amount` is then the DIFFERENCE, `listAmount` the full price. */
  upgradeFromPlanName?: string | null;
  /** The full list price, whatever `amount` says. */
  listAmount?: number | null;
  /** What this event pays — a decimal, already divided out of Stripe's minor units. Null when unreadable. */
  amount?: number | null;
  currency?: string | null;
  purchasable?: boolean | null;
  /** ROADMAP #60 — while the event's in-session offer is open: `amount` is the offer price, this what it costs without it. */
  amountBeforeOffer?: number | null;
  offerPercentOff?: number | null;
  offerEndsAt?: string | null;
  /** Seconds left on the SERVER's clock (`useOfferCountdown` counts down from it). */
  offerSecondsLeft?: number | null;
}

export class EventUpgradeService {
  /** The upgrades this brand sells for ONE event, priced, with "already upgraded" counted for that event. */
  static async catalog(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(
      UPGRADE_CATALOG_QUERY,
      { params: { addonType: "EVENT_PLAN", scopeRef: eventScopeOf(eventId) } },
      { fetchPolicy: "network-only" },
    );
  }

  /**
   * Starts paying for an upgrade of this event: a `clientSecret` for the PaymentElement (platform money — no connected
   * account). ⚠ A code covering the whole price answers `granted: true` and nothing is owed; the CLIENT sends a code,
   * never an amount.
   */
  static async createPaymentIntent(eventId: string, providerSku: string, discountCode?: string | null): Promise<OpResult> {
    const code = String(discountCode ?? "").trim();

    return ApiService.executeMutation(UPGRADE_INTENT_MUTATION, {
      params: { providerSku, scopeRef: eventScopeOf(eventId), ...(code ? { discountCode: code } : {}) },
    });
  }
}
